import React, { useEffect, useState } from "react";
import { api, errorMessage } from "../lib/api";
import { Loader2, Award, FileDown } from "lucide-react";
import { toast } from "sonner";
import PosNav from "./PosNav";

const rp = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");

export default function AdminPosCommission() {
    const today = new Date().toISOString().slice(0, 10);
    const monthAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
    const [from, setFrom] = useState(monthAgo);
    const [to, setTo] = useState(today);
    const [report, setReport] = useState({ rows: [] });
    const [loading, setLoading] = useState(false);
    const [commissionPct, setCommissionPct] = useState("");

    const fetchReport = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/pos/commission-report", { params: { from, to } });
            setReport(data || { rows: [] });
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { fetchReport(); }, [from, to]);  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/settings");
                setCommissionPct(data?.sales_commission_pct ?? "");
            } catch (err) {
                console.error("Failed to load commission settings:", err);
            }
        })();
    }, []);

    const saveCommissionPct = async () => {
        try {
            await api.put("/admin/settings", { sales_commission_pct: Number(commissionPct) || 0 });
            toast.success("Komisi % tersimpan");
        } catch (e) { toast.error(errorMessage(e)); }
    };

    const totalCommission = report.rows.reduce((s, r) => s + (r.total_commission || 0), 0);
    const totalSales = report.rows.reduce((s, r) => s + (r.total_sales || 0), 0);

    const exportCSV = () => {
        const header = "Sales Person,Invoices,Total Sales,Total Commission\n";
        const body = report.rows.map(r => `"${r.sales_person}",${r.invoices},${r.total_sales},${r.total_commission}`).join("\n");
        const blob = new Blob([header + body], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `komisi-${from}-to-${to}.csv`; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="page-container">
            <div className="mx-auto max-w-6xl px-4 py-8 sm:py-10">
                <PosNav title="Laporan Komisi Sales" subtitle="Total komisi per sales person berdasar invoice terbayar." />

                {/* Commission config */}
                <div className="rounded-lg border border-border bg-white p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    <div className="flex-1">
                        <div className="text-sm font-medium">Persen Komisi Default</div>
                        <div className="text-xs text-muted-foreground">Berlaku ke semua invoice terbayar yang punya <em>sales_person</em>. Otomatis dihitung saat invoice dibuat.</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <input data-testid="cm-pct-input" type="number" min="0" max="100" step="0.1" value={commissionPct} onChange={(e) => setCommissionPct(e.target.value)} placeholder="mis. 3" className="w-24 rounded-md border border-border bg-white px-3 py-2 text-sm" />
                        <span className="text-sm">%</span>
                        <button data-testid="cm-pct-save" onClick={saveCommissionPct} className="rounded-md bg-primary text-primary-foreground px-3 py-2 text-sm hover:opacity-90">Simpan</button>
                    </div>
                </div>

                {/* Filter */}
                <div className="rounded-lg border border-border bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
                    <label className="block"><span className="label-eyebrow text-muted-foreground">Dari</span><input data-testid="cm-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1.5 rounded-md border border-border bg-white px-3 py-2 text-sm" /></label>
                    <label className="block"><span className="label-eyebrow text-muted-foreground">Sampai</span><input data-testid="cm-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1.5 rounded-md border border-border bg-white px-3 py-2 text-sm" /></label>
                    <button data-testid="cm-refresh" onClick={fetchReport} className="rounded-md border border-border px-4 py-2 text-sm hover:bg-secondary">Refresh</button>
                    <button data-testid="cm-export" onClick={exportCSV} disabled={!report.rows.length} className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm hover:opacity-90 disabled:opacity-50"><FileDown size={13} /> Export CSV</button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <div className="rounded-lg border border-border bg-white p-4"><div className="text-xs text-muted-foreground">Total Penjualan (Periode)</div><div className="text-lg font-semibold">{rp(totalSales)}</div></div>
                    <div className="rounded-lg border border-border bg-white p-4"><div className="text-xs text-muted-foreground">Total Komisi (Periode)</div><div className="text-lg font-semibold text-primary">{rp(totalCommission)}</div></div>
                </div>

                {loading ? <div className="p-10 text-center text-muted-foreground"><Loader2 className="mx-auto animate-spin" /></div>
                    : report.rows.length === 0 ? <div className="p-10 text-center text-muted-foreground border border-dashed border-border rounded-lg"><Award className="mx-auto mb-3" size={32} strokeWidth={1.25} /> Belum ada data komisi di periode ini.</div>
                        : (
                            <div className="rounded-lg border border-border bg-white overflow-hidden">
                                <table className="w-full text-sm"><thead className="bg-secondary/60 text-xs uppercase text-muted-foreground"><tr><th className="px-3 py-2 text-left">Sales Person</th><th className="px-3 py-2 text-right">Invoices</th><th className="px-3 py-2 text-right">Total Penjualan</th><th className="px-3 py-2 text-right">Total Komisi</th></tr></thead>
                                    <tbody className="divide-y divide-border">
                                        {report.rows.map((r, i) => (
                                            <tr key={i} data-testid={`cm-row-${r.sales_person}`}>
                                                <td className="px-3 py-2 font-medium">{r.sales_person}</td>
                                                <td className="px-3 py-2 text-right">{r.invoices}</td>
                                                <td className="px-3 py-2 text-right">{rp(r.total_sales)}</td>
                                                <td className="px-3 py-2 text-right font-semibold text-primary">{rp(r.total_commission)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
            </div>
        </div>
    );
}
